import {
  BarChart,
  Bar,
  XAxis,
  YAxis,
  ResponsiveContainer,
  Cell,
  Tooltip,
  RadialBarChart,
  RadialBar,
  PolarAngleAxis,
} from 'recharts';
import {
  C,
  BAR_COLORS,
  cardStyle,
  SecTitle,
  BLabel,
  abreviar,
  pct,
  type FilterKey,
} from './dashboard-utils';

interface FichaStat {
  name: string;
  count: number;
  total: number;
}

interface IndPorTipo {
  tipo: string;
  cnt: number;
}

interface AggRole {
  name: string;
  value: number;
}

interface MadurezValues {
  gobernanza: number;
  documentacion: number;
  caracterizacion: number;
  indicadores: number;
  global: number;
}

interface DashboardMetricsProps {
  fichaStats: FichaStat[];
  totalIndicadores: number;
  conIndicador: number;
  mLen: number;
  indPorTipo: IndPorTipo[];
  aggRoles: AggRole[];
  gDocumentado: number;
  gPublicados: number;
  madurez: MadurezValues;
  madurezData: Array<{ name: string; value: number; fill: string }>;
  tog: (k: FilterKey, v: string) => void;
  dim: (k: FilterKey, v: string) => React.CSSProperties;
}

export function DashboardMetrics({
  fichaStats,
  totalIndicadores,
  conIndicador,
  mLen,
  indPorTipo,
  aggRoles,
  gDocumentado,
  gPublicados,
  madurez,
  madurezData,
  tog,
  dim,
}: DashboardMetricsProps) {
  return (
    <div
      style={{
        gridColumn: '1 / -1',
        display: 'grid',
        gridTemplateColumns: '1fr 1fr 1fr 0.8fr 1.2fr',
        gap: 5,
        minHeight: 0,
      }}
    >
      {/* Card A — Completitud Ficha Caracterizacion */}
      <div style={cardStyle}>
        <SecTitle>Completitud ficha</SecTitle>
        <div
          style={{
            flex: 1,
            minHeight: 0,
            overflowY: 'auto',
            display: 'flex',
            flexDirection: 'column',
            gap: 2,
          }}
        >
          {fichaStats.map((s) => {
            const p = pct(s.count, s.total);
            return (
              <div
                key={s.name}
                style={{ display: 'flex', alignItems: 'center', gap: 4, fontSize: 9 }}
              >
                <span
                  style={{
                    width: 72,
                    color: C.go,
                    fontWeight: 600,
                    overflow: 'hidden',
                    textOverflow: 'ellipsis',
                    whiteSpace: 'nowrap',
                  }}
                >
                  {s.name}
                </span>
                <div
                  style={{
                    flex: 1,
                    height: 8,
                    background: C.gc,
                    borderRadius: 4,
                    overflow: 'hidden',
                  }}
                >
                  <div
                    style={{
                      width: `${p}%`,
                      height: '100%',
                      background: p >= 80 ? C.statusOk : p >= 40 ? C.statusWarn : C.statusError,
                      borderRadius: 4,
                      transition: 'width .3s',
                    }}
                  />
                </div>
                <span
                  style={{
                    width: 32,
                    textAlign: 'right',
                    fontWeight: 700,
                    fontFamily: "'JetBrains Mono',monospace",
                    fontSize: 9,
                    color: p >= 80 ? C.statusOk : p >= 40 ? C.statusWarn : C.statusError,
                  }}
                >
                  {s.count}
                </span>
              </div>
            );
          })}
        </div>
      </div>

      {/* Card B — Indicadores de Proceso */}
      <div style={cardStyle}>
        <SecTitle>Indicadores</SecTitle>
        <div style={{ display: 'flex', gap: 8, marginBottom: 4 }}>
          <div>
            <div
              style={{
                fontSize: 18,
                fontWeight: 700,
                fontFamily: "'JetBrains Mono',monospace",
                color: C.a1,
              }}
            >
              {totalIndicadores}
            </div>
            <div style={{ fontSize: 7, color: C.go, textTransform: 'uppercase' }}>Total KPIs</div>
          </div>
          <div>
            <div
              style={{
                fontSize: 18,
                fontWeight: 700,
                fontFamily: "'JetBrains Mono',monospace",
                color: pct(conIndicador, mLen) >= 80 ? C.statusOk : C.statusWarn,
              }}
            >
              {conIndicador}/{mLen}
            </div>
            <div style={{ fontSize: 7, color: C.go, textTransform: 'uppercase' }}>Con KPI</div>
          </div>
        </div>
        <div
          style={{
            flex: 1,
            minHeight: 0,
            display: 'flex',
            flexWrap: 'wrap',
            gap: 3,
            alignContent: 'flex-start',
          }}
        >
          {indPorTipo.map((t) => (
            <span
              key={t.tipo}
              onClick={() => tog('rol', t.tipo)}
              style={{
                display: 'inline-flex',
                alignItems: 'center',
                gap: 3,
                background: C.as,
                color: C.a1,
                fontSize: 8,
                fontWeight: 700,
                padding: '2px 6px',
                borderRadius: 8,
                cursor: 'pointer',
              }}
            >
              {t.tipo.replace('AUTO_', '')}{' '}
              <span style={{ fontFamily: "'JetBrains Mono',monospace", fontWeight: 800 }}>
                {t.cnt}
              </span>
            </span>
          ))}
        </div>
      </div>

      {/* Card C — Roles Involucrados */}
      <div style={cardStyle}>
        <SecTitle>Roles involucrados</SecTitle>
        {aggRoles.length === 0 ? (
          <div
            style={{
              flex: 1,
              display: 'flex',
              alignItems: 'center',
              justifyContent: 'center',
              color: C.go,
              fontStyle: 'italic',
              fontSize: 11,
            }}
          >
            Sin datos
          </div>
        ) : (
          <div style={{ flex: 1, minHeight: 0 }}>
            <ResponsiveContainer width="100%" height="100%">
              <BarChart
                data={aggRoles}
                layout="vertical"
                margin={{ left: 240, right: 20, top: 8, bottom: 4 }}
              >
                <XAxis type="number" hide />
                <YAxis
                  type="category"
                  dataKey="name"
                  width={240}
                  tick={{ fontSize: 11 }}
                  tickLine={false}
                  axisLine={false}
                  interval={0}
                  tickFormatter={(v: string) => abreviar(v, 50)}
                />
                <Tooltip contentStyle={{ fontSize: 11, fontFamily: "'DM Sans',sans-serif" }} />
                <Bar
                  dataKey="value"
                  label={<BLabel />}
                  radius={[0, 3, 3, 0]}
                  barSize={12}
                  onClick={(_: unknown, idx: number) => {
                    const item = aggRoles[idx];
                    if (item) tog('rol', item.name);
                  }}
                  style={{ cursor: 'pointer' }}
                >
                  {aggRoles.map((entry, i) => (
                    <Cell
                      key={i}
                      fill={BAR_COLORS[i % BAR_COLORS.length]}
                      style={dim('rol', entry.name)}
                    />
                  ))}
                </Bar>
              </BarChart>
            </ResponsiveContainer>
          </div>
        )}
      </div>

      {/* Card Documentacion — 2 Gauges */}
      <div style={cardStyle}>
        <SecTitle>Documentacion</SecTitle>
        <div
          style={{
            flex: 1,
            display: 'flex',
            flexDirection: 'column',
            alignItems: 'center',
            justifyContent: 'center',
            gap: 4,
          }}
        >
          {/* Gauge Documentado */}
          <div style={{ textAlign: 'center' }}>
            <RadialBarChart
              width={90}
              height={55}
              cx={45}
              cy={50}
              innerRadius={22}
              outerRadius={34}
              startAngle={180}
              endAngle={0}
              data={[
                {
                  v: gDocumentado,
                  fill:
                    gDocumentado >= 80
                      ? C.statusOk
                      : gDocumentado >= 50
                        ? C.statusWarn
                        : C.statusError,
                },
              ]}
              barSize={6}
            >
              <PolarAngleAxis type="number" domain={[0, 100]} tick={false} />
              <RadialBar background={{ fill: C.gc }} dataKey="v" cornerRadius={3} />
            </RadialBarChart>
            <div
              style={{
                marginTop: -6,
                fontSize: 14,
                fontWeight: 700,
                fontFamily: "'JetBrains Mono',monospace",
                color:
                  gDocumentado >= 80
                    ? C.statusOk
                    : gDocumentado >= 50
                      ? C.statusWarn
                      : C.statusError,
              }}
            >
              {gDocumentado}%
            </div>
            <div style={{ fontSize: 7, color: C.go, fontWeight: 600, textTransform: 'uppercase' }}>
              Documentado
            </div>
          </div>
          {/* Gauge Publicados */}
          <div style={{ textAlign: 'center' }}>
            <RadialBarChart
              width={90}
              height={55}
              cx={45}
              cy={50}
              innerRadius={22}
              outerRadius={34}
              startAngle={180}
              endAngle={0}
              data={[
                {
                  v: gPublicados,
                  fill:
                    gPublicados >= 80
                      ? C.statusOk
                      : gPublicados >= 50
                        ? C.statusWarn
                        : C.statusError,
                },
              ]}
              barSize={6}
            >
              <PolarAngleAxis type="number" domain={[0, 100]} tick={false} />
              <RadialBar background={{ fill: C.gc }} dataKey="v" cornerRadius={3} />
            </RadialBarChart>
            <div
              style={{
                marginTop: -6,
                fontSize: 14,
                fontWeight: 700,
                fontFamily: "'JetBrains Mono',monospace",
                color:
                  gPublicados >= 80 ? C.statusOk : gPublicados >= 50 ? C.statusWarn : C.statusError,
              }}
            >
              {gPublicados}%
            </div>
            <div style={{ fontSize: 7, color: C.go, fontWeight: 600, textTransform: 'uppercase' }}>
              Publicados
            </div>
          </div>
        </div>
      </div>

      {/* Card D — Indice de Madurez */}
      <div style={cardStyle}>
        <SecTitle>Indice de madurez</SecTitle>
        <div style={{ flex: 1, display: 'flex', alignItems: 'center', gap: 4, minHeight: 0 }}>
          <div style={{ position: 'relative', width: 130, height: '100%', flexShrink: 0 }}>
            <ResponsiveContainer width="100%" height="100%">
              <RadialBarChart
                innerRadius="25%"
                outerRadius="95%"
                data={madurezData}
                startAngle={180}
                endAngle={0}
                barSize={8}
              >
                <PolarAngleAxis type="number" domain={[0, 100]} tick={false} />
                <RadialBar background={{ fill: C.gc }} dataKey="value" cornerRadius={4} />
              </RadialBarChart>
            </ResponsiveContainer>
            <div
              style={{
                position: 'absolute',
                bottom: 4,
                left: '50%',
                transform: 'translateX(-50%)',
                fontSize: 20,
                fontWeight: 700,
                fontFamily: "'JetBrains Mono',monospace",
                color:
                  madurez.global >= 80
                    ? C.statusOk
                    : madurez.global >= 50
                      ? C.statusWarn
                      : C.statusError,
              }}
            >
              {madurez.global}%
            </div>
          </div>
          <div style={{ display: 'flex', flexDirection: 'column', gap: 3, fontSize: 8 }}>
            {madurezData
              .slice()
              .reverse()
              .map((d) => (
                <div key={d.name} style={{ display: 'flex', alignItems: 'center', gap: 3 }}>
                  <span
                    style={{
                      width: 6,
                      height: 6,
                      borderRadius: 1,
                      background: d.fill,
                      display: 'inline-block',
                      flexShrink: 0,
                    }}
                  />
                  <span style={{ color: C.go, fontWeight: 600 }}>{d.name}</span>
                  <span
                    style={{
                      fontFamily: "'JetBrains Mono',monospace",
                      fontWeight: 700,
                      color: d.fill,
                    }}
                  >
                    {d.value}%
                  </span>
                </div>
              ))}
          </div>
        </div>
      </div>
    </div>
  );
}
